import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Image as ImageIcon, FileText, File, Loader2, Sparkles, X, Download, Eye, CheckCircle2,
  Ruler,
} from "lucide-react";

function isImage(ct) { return (ct || "").startsWith("image/"); }
function isPdf(ct) { return (ct || "") === "application/pdf"; }
function isPlannable(ct) { return isImage(ct) || isPdf(ct); }

/**
 * Thumbnail tile + preview lightbox for a single file.
 * Lazy-loads image content as authenticated blob URL so we never leak tokens in <img src>.
 */
export function FileTile({ file, onOpen, onAnalyze, analyzing, analysis, onTakeoff, takingOff, takeoff }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const Icon = isImage(file.content_type) ? ImageIcon : isPdf(file.content_type) ? FileText : File;

  useEffect(() => {
    let canceled = false; let url = null;
    if (isImage(file.content_type)) {
      api.get(`/files/${file.id}/download`, { responseType: "blob" })
        .then((r) => {
          if (canceled) return;
          url = URL.createObjectURL(r.data);
          setBlobUrl(url);
        })
        .catch(() => {});
    }
    return () => { canceled = true; if (url) URL.revokeObjectURL(url); };
  }, [file.id, file.content_type]);

  return (
    <div className="bq-card overflow-hidden flex flex-col" data-testid={`file-tile-${file.id}`}>
      <button
        onClick={() => onOpen(file)}
        data-testid={`file-open-${file.id}`}
        className="block w-full aspect-[4/3] bg-[var(--bq-bg)] relative group focus:outline-none"
      >
        {blobUrl ? (
          <img src={blobUrl} alt={file.original_filename} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[var(--bq-text-muted)]">
            <Icon size={32} />
            <span className="overline mt-2">{isPdf(file.content_type) ? "PDF" : "FILE"}</span>
          </div>
        )}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="bg-white px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1">
            <Eye size={12} /> Preview
          </span>
        </span>
      </button>
      <div className="px-3 py-3 text-sm border-t border-[var(--bq-border)]">
        <div className="font-medium truncate" title={file.original_filename}>{file.original_filename}</div>
        <div className="text-xs text-[var(--bq-text-muted)] flex justify-between mt-1">
          <span>{Math.round(file.size / 1024)} KB</span>
          <span className="truncate">{file.content_type?.split("/")[1] || "file"}</span>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => onAnalyze(file)} disabled={analyzing}
            data-testid={`file-analyze-${file.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-[var(--bq-border)] hover:border-[var(--bq-orange)] hover:text-[var(--bq-orange)] disabled:opacity-50 transition-all"
          >
            {analyzing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI analyze
          </button>
          {isPlannable(file.content_type) && (
            <button
              onClick={() => onTakeoff(file)} disabled={takingOff}
              data-testid={`file-takeoff-${file.id}`}
              className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-[var(--bq-border)] hover:border-[var(--bq-navy)] hover:text-[var(--bq-navy)] disabled:opacity-50 transition-all"
              title="Vision-based measurement takeoff from this plan"
            >
              {takingOff ? <Loader2 size={12} className="animate-spin" /> : <Ruler size={12} />} Plan takeoff
            </button>
          )}
        </div>
        {analysis && (
          <div className="mt-3 text-xs bg-[var(--bq-bg)] border border-dashed border-[var(--bq-border)] rounded-md p-2.5" data-testid={`file-analysis-${file.id}`}>
            <div className="flex items-center gap-1 text-[var(--bq-green)] font-semibold mb-1">
              <CheckCircle2 size={12} /> AI analysis
            </div>
            {analysis.summary && <p className="text-[var(--bq-text-muted)] leading-snug">{analysis.summary}</p>}
            {analysis.rooms?.length > 0 && (
              <div className="mt-1.5"><b>Rooms:</b> {analysis.rooms.join(", ")}</div>
            )}
            {analysis.dimensions?.length > 0 && (
              <div className="mt-1"><b>Dimensions:</b> {analysis.dimensions.join("; ")}</div>
            )}
            {analysis.elements?.length > 0 && (
              <div className="mt-1"><b>Elements:</b> {analysis.elements.join(", ")}</div>
            )}
          </div>
        )}
        {takeoff && <TakeoffPanel takeoff={takeoff} fileId={file.id} />}
      </div>
    </div>
  );
}

function TakeoffPanel({ takeoff, fileId }) {
  const conf = takeoff.confidence || 0;
  const confColor = conf >= 70 ? "var(--bq-green)" : conf >= 40 ? "var(--bq-amber)" : "var(--bq-red)";
  return (
    <div
      className="mt-3 text-xs bg-[var(--bq-bg)] border border-dashed border-[var(--bq-navy)] rounded-md p-2.5"
      data-testid={`file-takeoff-result-${fileId}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1 font-semibold text-[var(--bq-navy)]">
          <Ruler size={12} /> Plan takeoff
          {takeoff.drawing_type && takeoff.drawing_type !== "unknown" && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--bq-navy)] text-white text-[10px] uppercase tracking-wider">
              {takeoff.drawing_type}
            </span>
          )}
        </div>
        <span className="font-mono font-bold" style={{ color: confColor }}>
          {conf}% conf
        </span>
      </div>
      {takeoff.summary && (
        <p className="text-[var(--bq-text-muted)] leading-snug mb-2">{takeoff.summary}</p>
      )}
      <div className="grid grid-cols-2 gap-1.5 mt-1">
        {takeoff.scale_detected && (
          <KV label="Scale" value={takeoff.scale_detected} />
        )}
        {takeoff.total_floor_area_m2 != null && (
          <KV label="Floor area" value={`${takeoff.total_floor_area_m2} m²`} />
        )}
        {takeoff.levels != null && <KV label="Levels" value={takeoff.levels} />}
        {takeoff.openings && (
          <KV
            label="Openings"
            value={`${takeoff.openings.doors || 0} doors · ${takeoff.openings.windows || 0} windows`}
          />
        )}
        {takeoff.walls?.external_m != null && (
          <KV label="Ext walls" value={`${takeoff.walls.external_m} m`} />
        )}
        {takeoff.walls?.internal_m != null && (
          <KV label="Int walls" value={`${takeoff.walls.internal_m} m`} />
        )}
      </div>
      {takeoff.rooms?.length > 0 && (
        <div className="mt-2">
          <div className="overline mb-1">Rooms detected</div>
          <ul className="space-y-0.5">
            {takeoff.rooms.map((r, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="truncate">{r.name}</span>
                <span className="text-[var(--bq-text-muted)] font-mono text-[11px]">
                  {r.area_m2 != null ? `${r.area_m2} m²` : ""}
                  {r.dimensions ? ` · ${r.dimensions}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {takeoff.fixtures?.length > 0 && (
        <div className="mt-2">
          <b>Fixtures:</b>{" "}
          <span className="text-[var(--bq-text-muted)]">{takeoff.fixtures.join(", ")}</span>
        </div>
      )}
      {takeoff.warnings?.length > 0 && (
        <div className="mt-2 text-[var(--bq-red)]">
          <b>Warnings:</b> {takeoff.warnings.join(" · ")}
        </div>
      )}
      {takeoff.assumptions?.length > 0 && (
        <div className="mt-1 text-[var(--bq-text-muted)] italic">
          Assumptions: {takeoff.assumptions.join("; ")}
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="overline text-[10px]">{label}</span>
      <span className="font-mono text-xs">{value}</span>
    </div>
  );
}

/**
 * Lightbox / modal previewer for an image or PDF file.
 * Renders authenticated blob URLs so cookies are sent and tokens never leak.
 */
export function FilePreviewModal({ file, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!file) return;
    let url = null; let canceled = false;
    setErr("");
    api.get(`/files/${file.id}/download`, { responseType: "blob" })
      .then((r) => {
        if (canceled) return;
        url = URL.createObjectURL(r.data);
        if (!canceled) setBlobUrl(url);
      })
      .catch((e) => { if (!canceled) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); });
    return () => { canceled = true; if (url) URL.revokeObjectURL(url); setBlobUrl(null); };
  }, [file]);

  if (!file) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="file-preview-modal">
      <div className="bq-card w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[var(--bq-border)] flex items-center justify-between">
          <div>
            <div className="overline">Preview</div>
            <div className="font-display text-lg font-bold truncate max-w-md">{file.original_filename}</div>
          </div>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a href={blobUrl} download={file.original_filename} className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--bq-border)] rounded-md hover:border-[var(--bq-navy)]" data-testid="preview-download-btn">
                <Download size={12} /> Download
              </a>
            )}
            <button onClick={onClose} className="p-2 rounded-md hover:bg-[var(--bq-bg)]" data-testid="preview-close-btn">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[var(--bq-bg)] flex items-center justify-center min-h-[60vh]">
          {err && <div className="text-sm text-[var(--bq-red)]">{err}</div>}
          {!err && !blobUrl && <Loader2 className="animate-spin text-[var(--bq-text-muted)]" />}
          {blobUrl && isImage(file.content_type) && (
            <img src={blobUrl} alt={file.original_filename} className="max-w-full max-h-[80vh] object-contain" />
          )}
          {blobUrl && isPdf(file.content_type) && (
            <iframe src={blobUrl} title={file.original_filename} className="w-full h-[80vh] border-0" data-testid="pdf-iframe" />
          )}
          {blobUrl && !isImage(file.content_type) && !isPdf(file.content_type) && (
            <div className="text-sm text-[var(--bq-text-muted)] p-8 text-center">
              No inline preview for this file type.<br />
              <a href={blobUrl} download={file.original_filename} className="text-[var(--bq-orange)] font-semibold mt-2 inline-block">Download instead</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Files panel: grid of FileTiles + lightbox + analyze flow.
 */
export default function ProjectFiles({ projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently previewed file
  const [analyses, setAnalyses] = useState({}); // {fileId: analysis}
  const [analyzing, setAnalyzing] = useState({}); // {fileId: bool}
  const [takeoffs, setTakeoffs] = useState({}); // {fileId: takeoff}
  const [takingOff, setTakingOff] = useState({}); // {fileId: bool}

  useEffect(() => {
    api.get(`/projects/${projectId}/files`)
      .then((r) => {
        setFiles(r.data);
        const initial = {};
        const initTake = {};
        r.data.forEach((f) => {
          if (f.analysis) initial[f.id] = f.analysis;
          if (f.takeoff) initTake[f.id] = f.takeoff;
        });
        setAnalyses(initial);
        setTakeoffs(initTake);
      })
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const onAnalyze = async (file) => {
    setAnalyzing((s) => ({ ...s, [file.id]: true }));
    try {
      const { data } = await api.post(`/files/${file.id}/analyze`);
      setAnalyses((s) => ({ ...s, [file.id]: data }));
    } catch (e) {
      setAnalyses((s) => ({ ...s, [file.id]: { summary: "Analysis failed.", dimensions: [], rooms: [], elements: [] } }));
    } finally {
      setAnalyzing((s) => ({ ...s, [file.id]: false }));
    }
  };

  const onTakeoff = async (file) => {
    setTakingOff((s) => ({ ...s, [file.id]: true }));
    try {
      const { data } = await api.post(`/files/${file.id}/takeoff`);
      setTakeoffs((s) => ({ ...s, [file.id]: data }));
    } catch (e) {
      setTakeoffs((s) => ({
        ...s,
        [file.id]: {
          summary: formatApiErrorDetail(e.response?.data?.detail) || "Takeoff failed.",
          confidence: 0, rooms: [], openings: { doors: 0, windows: 0 },
          walls: {}, fixtures: [], assumptions: [], warnings: ["Takeoff failed — please retry."],
        },
      }));
    } finally {
      setTakingOff((s) => ({ ...s, [file.id]: false }));
    }
  };

  if (loading) return <div className="overline">Loading files…</div>;
  if (files.length === 0) return (
    <div className="bq-card p-10 text-center" data-testid="no-files">
      <File size={28} className="mx-auto text-[var(--bq-text-muted)]" />
      <div className="overline mt-3">No files attached</div>
      <p className="text-sm text-[var(--bq-text-muted)] mt-2">Files uploaded during project submission appear here.</p>
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {files.map((f) => (
          <FileTile
            key={f.id} file={f}
            onOpen={setActive}
            onAnalyze={onAnalyze}
            analyzing={!!analyzing[f.id]}
            analysis={analyses[f.id]}
            onTakeoff={onTakeoff}
            takingOff={!!takingOff[f.id]}
            takeoff={takeoffs[f.id]}
          />
        ))}
      </div>
      <FilePreviewModal file={active} onClose={() => setActive(null)} />
    </>
  );
}
